/**
 * Âmedorée — Size chart drawer
 *
 * Opens on any element carrying [data-size-chart-open]. Its value is the product id,
 * and opens the dialog with the same [data-size-chart-dialog] value (the last one
 * on the page, so a quick view wins over the product page behind it).
 *
 * The variant picker is in Stretch's partial-rerender list, so this file can
 * be injected more than once on a single page view. The guard below stops the
 * click handlers being bound twice (which would open and immediately reopen
 * the drawer).
 *
 * When it opens, the row for the size picked on the page is shaded. The size
 * is read from the checked option in the same picker as the trigger, so a quick
 * view marks its own pick.
 *
 * Exposes window.AmedoreeSizeChart.open() / .close() for your own code.
 */
(function () {
  'use strict';

  if (window.AmedoreeSizeChart) return;

  var ANIMATION_MS = 340;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getDialog(id) {
    var all = document.querySelectorAll(id ? '[data-size-chart-dialog="' + id + '"]' : '[data-size-chart-dialog]');
    if (!all.length && id) all = document.querySelectorAll('[data-size-chart-dialog]');
    return all.length ? all[all.length - 1] : null;
  }

  function openDialog() {
    return document.querySelector('[data-size-chart-dialog][open]');
  }

  function sizeKey(value) {
    return String(value || '').toLowerCase().replace(/\s+/g, '');
  }

  function pickedSize(trigger) {
    var scope = trigger && trigger.closest('fieldset, .variant-picker__option');
    if (!scope && trigger) scope = trigger.closest('variant-picker, form, .product-info, .quick-buy-modal');
    if (!scope) return '';
    var input = scope.querySelector('input[type="radio"]:checked');
    if (!input) return '';
    // The radio's value is the option value id, so read the visible label
    var label = null;
    if (input.id) {
      var safeId = window.CSS && CSS.escape ? CSS.escape(input.id) : input.id;
      label = scope.querySelector('label[for="' + safeId + '"]');
    }
    if (!label) label = input.nextElementSibling;
    return label ? label.textContent.trim() : '';
  }

  function markSize(dialog, trigger) {
    var picked = sizeKey(pickedSize(trigger));
    dialog.querySelectorAll('tr[data-size]').forEach(function (row) {
      var key = row.getAttribute('data-size');
      var match = !!picked && (key === picked || key.split('/').indexOf(picked) > -1 || picked.split('/').indexOf(key) > -1);
      row.classList.toggle('is-selected', match);
    });
  }

  function open(id, trigger) {
    var dialog = getDialog(id);
    if (!dialog || dialog.open) return;

    markSize(dialog, trigger);

    dialog.showModal();
    // Force a frame so the closed transform is committed before animating in.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        dialog.classList.add('is-open');
      });
    });
  }

  function close(target) {
    var dialog = target || openDialog();
    if (!dialog || !dialog.open) return;

    dialog.classList.remove('is-open');
    window.setTimeout(
      function () {
        if (dialog.open) dialog.close();
      },
      prefersReducedMotion() ? 0 : ANIMATION_MS
    );
  }

  function convertUnits(dialog, unit) {
    var table = dialog.querySelector('[data-size-chart-table]');
    if (!table) return;

    table.querySelectorAll('td[data-cm]').forEach(function (cell) {
      var source = cell.getAttribute('data-cm');

      if (unit === 'cm') {
        cell.textContent = source;
        return;
      }

      // Convert every number found in the cell, so ranges ("96-101") and
      // suffixed values ("96 cm") both survive intact.
      cell.textContent = source.replace(/\d+(?:[.,]\d+)?/g, function (match) {
        var value = parseFloat(match.replace(',', '.'));
        if (isNaN(value)) return match;
        return (Math.round((value / 2.54) * 10) / 10).toString();
      });
    });
  }

  function setUnit(dialog, unit) {
    dialog.querySelectorAll('[data-size-chart-unit]').forEach(function (button) {
      var isActive = button.getAttribute('data-size-chart-unit') === unit;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    convertUnits(dialog, unit);

    var tolerance = dialog.querySelector('[data-size-chart-tolerance]');
    if (tolerance) tolerance.textContent = tolerance.getAttribute(unit === 'in' ? 'data-in' : 'data-cm');
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-size-chart-open]');
    if (trigger) {
      event.preventDefault();
      event.stopPropagation();
      open(trigger.getAttribute('data-size-chart-open'), trigger);
      return;
    }

    var closeButton = event.target.closest('[data-size-chart-close]');
    if (closeButton) {
      event.preventDefault();
      event.stopPropagation();
      close(closeButton.closest('[data-size-chart-dialog]'));
      return;
    }

    var unitButton = event.target.closest('[data-size-chart-unit]');
    if (unitButton) {
      event.preventDefault();
      setUnit(unitButton.closest('[data-size-chart-dialog]'), unitButton.getAttribute('data-size-chart-unit'));
      return;
    }

    // A click on the dialog element itself is a click on the backdrop area,
    // since the visible panel is a child that stops the hit test there.
    if (event.target.matches && event.target.matches('[data-size-chart-dialog]') && event.target.open) {
      event.stopPropagation();
      close(event.target);
    }
  });

  // Escape closes only the size chart, not the quick view behind it (that one listens
  // on the document, so this runs first, on the window)
  window.addEventListener(
    'keydown',
    function (event) {
      if (event.key !== 'Escape' && event.key !== 'Esc') return;
      var dialog = openDialog();
      if (!dialog) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      close(dialog);
    },
    true
  );

  // Escape: intercept so the panel animates out rather than vanishing.
  document.addEventListener('cancel', function (event) {
    if (!event.target.matches || !event.target.matches('[data-size-chart-dialog]')) return;
    event.preventDefault();
    event.stopPropagation();
    close(event.target);
  }, true);

  window.AmedoreeSizeChart = { open: open, close: close };
})();
